import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Button,
  Input,
  Modal,
  Form,
  message,
  ConfigProvider,
  Popconfirm,
  Space,
  Image,
  Select,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import "./subCategories.css";
import { routes } from "../../api/routes";
import { callApi } from "../../api/apiCaller";
import Loader from "../../components/loader/loader";
import ImageUpload from "../../components/imageUpload/imageUpload";
import NotificationBell from "../../components/notificationBell/notificationBell";
import dayjs from "dayjs";

const SubCategories = () => {
  const navigate = useNavigate();
  const [subCategories, setSubCategories] = useState([]);
  const [allSubCategories, setAllSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    getCategories();
    getSubCategories();
  }, []);

  const getCategories = () => {
    let getRes = (res) => {
      if (res.status === 200) {
        // Handle response structure: res.data.data.data for moment-category API
        const categoriesData = res.data?.data?.data || res.data?.data || res.data || [];
        setCategories(categoriesData);
      }
    };

    callApi(
      "GET",
      routes.getAllMomentCategories,
      null,
      setIsLoading,
      getRes,
      (error) => {
        // Categories loading failed - subcategories may still work
      }
    );
  };

  const getSubCategories = () => {
    let getRes = (res) => {
      if (res.status === 200) {
        // Handle response structure: res.data.data.data for moment-sub-category API
        const subCategoriesData = res.data?.data?.data || res.data?.data || res.data || [];
        setAllSubCategories(subCategoriesData);
        if (searchText) {
          const lower = searchText.toLowerCase();
          setSubCategories(
            subCategoriesData.filter((subCat) =>
              (subCat.title || "").toLowerCase().includes(lower)
            )
          );
        } else {
          setSubCategories(subCategoriesData);
        }
      }
    };

    callApi(
      "GET",
      routes.getAllSubCategories,
      null,
      setIsLoading,
      getRes,
      (error) => {
        message.error("Failed to load subcategories");
      }
    );
  };

  const handleCreate = () => {
    setEditingSubCategory(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (subCategory) => {
    setEditingSubCategory(subCategory);
    form.setFieldsValue({
      title: subCategory.title,
      image: subCategory.image,
      momentCategory: subCategory.momentCategory?._id || subCategory.momentCategory,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (subCategoryId) => {
    let getRes = (res) => {
      if (res.status === 200 || res.status === 201) {
        message.success("SubCategory deleted successfully");
        getSubCategories();
      } else {
        message.warning(`Delete completed with status ${res.status}`);
        getSubCategories();
      }
    };

    callApi(
      "DELETE",
      `${routes.deleteSubCategory}/${subCategoryId}`,
      null,
      setIsLoading,
      getRes,
      (error) => {
        const errorMsg = error?.response?.data?.message || error?.message || "Failed to delete subcategory";
        message.error(errorMsg);
      }
    );
  };


  const handleSubmit = (values) => {
    // Close modal first, then proceed with API call
    setIsModalVisible(false);
    form.resetFields();
    const currentEditingSubCategory = editingSubCategory;
    setEditingSubCategory(null);
    
    if (currentEditingSubCategory) {
      // Update
      let getRes = (res) => {
        if (res.status === 200 || res.status === 201) {
          message.success("SubCategory updated successfully");
          getSubCategories();
        } else {
          message.warning(`Update completed with status ${res.status}`);
          getSubCategories();
        }
      };

      // Update sends title, image, and momentCategory if changed
      const updateData = {
        title: values.title,
        image: values.image,
      };

      // Include momentCategory if it's provided and different from current
      if (values.momentCategory) {
        updateData.momentCategory = values.momentCategory;
      }

      callApi(
        "PATCH",
        `${routes.updateSubCategory}/${currentEditingSubCategory._id}`,
        updateData,
        setIsLoading,
        getRes,
        (error) => {
          const errorMsg = error?.response?.data?.message || error?.message || "Failed to update subcategory";
          message.error(errorMsg);
        }
      );
    } else {
      // Create
      if (!values.momentCategory) {
        message.error("Please select a category");
        setIsModalVisible(true);
        return;
      }

      let getRes = (res) => {
        if (res.status === 200 || res.status === 201) {
          message.success("SubCategory created successfully");
          getSubCategories();
        } else {
          message.warning(`Create completed with status ${res.status}`);
          getSubCategories();
        }
      };

      // Create sends title and image, with momentCategory ID in URL
      const createData = {
        title: values.title,
        image: values.image,
      };

      callApi(
        "POST",
        `${routes.createSubCategory}/${values.momentCategory}`,
        createData,
        setIsLoading,
        getRes,
        (error) => {
          const errorMsg = error?.response?.data?.message || error?.message || "Failed to create subcategory";
          message.error(errorMsg);
        }
      );
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingSubCategory(null);
  };

  const handleSearch = (value) => {
    const text = value || "";
    setSearchText(text);

    if (!text) {
      setSubCategories(allSubCategories);
      return;
    }

    const lower = text.toLowerCase();
    setSubCategories(
      allSubCategories.filter((subCat) =>
        (subCat.title || "").toLowerCase().includes(lower)
      )
    );
  };

  const columns = [
    {
      title: "No",
      key: "index",
      render: (text, record, index) => index + 1,
      width: 60,
    },
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      width: 100,
      render: (image) => (
        <Image
          src={image}
          alt="SubCategory"
          width={60}
          height={60}
          style={{ objectFit: "cover", borderRadius: 4 }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29BwNZwQxT0g2F4U0B0J9BsKxO4L4J4BdGTGMjQCYQe4B"
        />
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text) => <span style={{ fontWeight: 500, color: "#333" }}>{text || "N/A"}</span>,
    },
    {
      title: "Category",
      dataIndex: "momentCategory",
      key: "momentCategory",
      render: (momentCategory) => (
        <span style={{ color: "#666" }}>
          {momentCategory?.title || momentCategory || "N/A"}
        </span>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "N/A"),
      width: 120,
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete SubCategory"
            description="Are you sure you want to delete this subcategory?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#FF66B2",
        },
      }}
    >
      <div className="subcategories-container">
        <Loader loading={isLoading} />
        <div className="subcategories-header">
          <h1 className="subcategories-title">SubCategory Management</h1>
          <NotificationBell />
        </div>

        <div className="subcategories-content">
          <Card className="subcategories-card">
            <div className="subcategories-actions">
              <Input
                placeholder="Search subcategories by title..."
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => handleSearch(e.target.value)}
                style={{ maxWidth: 300 }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
                size="middle"
              >
                Add SubCategory
              </Button>
            </div>

            <Table
              columns={columns}
              dataSource={subCategories}
              rowKey="_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} subcategories`,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              className="subcategories-table"
            />
          </Card>
        </div>

        <Modal
          title={editingSubCategory ? "Edit SubCategory" : "Add New SubCategory"}
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            className="subcategory-form"
          >
            <Form.Item
              label="Category"
              name="momentCategory"
              rules={[{ required: true, message: "Please select a category" }]}
            >
              <Select
                placeholder="Select a category"
                size="large"
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={categories.map((cat) => ({
                  value: cat._id,
                  label: cat.title,
                }))}
              />
            </Form.Item>

            <Form.Item
              label="SubCategory Title"
              name="title"
              rules={[
                { required: true, message: "Please enter subcategory title" },
                { min: 2, message: "Title must be at least 2 characters" },
                { max: 50, message: "Title must be less than 50 characters" },
              ]}
            >
              <Input placeholder="Enter subcategory title" size="large" />
            </Form.Item>

            <Form.Item
              label="SubCategory Image"
              name="image"
              rules={[{ required: true, message: "Please upload subcategory image" }]}
            >
              <ImageUpload maxSize={5} />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" size="large">
                  {editingSubCategory ? "Update" : "Create"}
                </Button>
                <Button onClick={handleCancel} size="large">
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default SubCategories;
